import axios from "axios";
import { enqueueMutation, drainQueue } from "./offlineQueue";

// Emergent previews proxy /api on their own origin. A copied build-time preview
// URL must not send member credentials to a different preview environment.
const isEmergentPreview = typeof window !== "undefined"
  && window.location.hostname.endsWith(".preview.emergentagent.com");
const BACKEND_URL = isEmergentPreview ? "" : (process.env.REACT_APP_BACKEND_URL || "");
// Do NOT add /api here - it will be added in individual route calls
export const API = BACKEND_URL;

const TOKEN_KEY = "gio_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  // Check for both admin token (gio_token) and member token (memberToken)
  const adminToken = getToken();
  const memberToken = localStorage.getItem("memberToken");
  
  // Use memberToken if it exists and we're on a member route, otherwise use admin token
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const isMemberRoute = currentPath.startsWith('/members') || currentPath.startsWith('/store')
    || currentPath.startsWith('/video/') || currentPath.startsWith('/cast/');
  
  const token = (isMemberRoute && memberToken) ? memberToken : adminToken;
  
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const isMutation = (method) => ["post", "put", "patch", "delete"].includes((method || "").toLowerCase());
const isOfflineError = (err) =>
  !navigator.onLine ||
  err?.code === "ERR_NETWORK" ||
  err?.message === "Network Error" ||
  (err?.response?.status === 503 && err?.response?.data?.offline === true);

api.interceptors.response.use(
  (r) => {
    // AUTO-REFRESH v2.0: Trigger global UI refresh after successful mutations
    const isAdminOTPVerify = /\/api\/admin-otp\/verify(?:\?|$)/.test(r.config?.url || "");
    if (r.config && isMutation(r.config.method) && !isAdminOTPVerify) {
      // Extract resource type from URL and trigger global refresh
      const resourceType = extractResourceType(r.config.url);
      if (resourceType) {
        // Delay slightly to ensure backend has processed the change
        setTimeout(() => {
          console.log(`[API] 🔄 Mutation complete - triggering global refresh for: ${resourceType}`);
          
          // Dispatch to both window and custom event bus for maximum compatibility
          window.dispatchEvent(new CustomEvent('gio:data-changed', { 
            detail: { 
              resourceType, 
              method: r.config.method, 
              url: r.config.url,
              timestamp: Date.now()
            }
          }));
          
          // Also trigger wildcard for components listening to all changes
          if (resourceType !== '*') {
            window.dispatchEvent(new CustomEvent('gio:data-changed', { 
              detail: { resourceType: '*', timestamp: Date.now() }
            }));
          }
        }, 50);
      }
    }
    return r;
  },
  async (err) => {
    // Sanitize Pydantic validation errors before they reach components
    if (err?.response?.status === 422 && err?.response?.data?.detail) {
      const detail = err.response.data.detail;
      if (Array.isArray(detail)) {
        // Convert Pydantic validation error array to a readable string
        const errorMessages = detail.map(e => {
          const location = Array.isArray(e.loc) ? e.loc.join('.') : '';
          return location ? `${location}: ${e.msg || e.type}` : (e.msg || e.type || 'Validation error');
        }).join('; ');
        // Replace the detail array with a string so components can safely display it
        err.response.data.detail = errorMessages || 'Validation error';
      }
    }
    
    if (err?.response?.status === 401) {
      // Allow specific calls to opt out of the global redirect (e.g. background
      // fetches like the Cloudinary library list) so a transient 401 doesn't kick
      // the user out of /crm/landing mid-edit. Set `_silent401: true` on the
      // axios config to opt out.
      const silent = err?.config?._silent401;
      if (!silent) {
        // Check if user is in member context
        const memberToken = localStorage.getItem('memberToken');
        const currentPath = window.location.pathname;
        const isMemberRoute = currentPath.startsWith('/members') || currentPath.startsWith('/store');
        
        if (memberToken && isMemberRoute) {
          // Member context: clear member token and redirect to member login
          localStorage.removeItem('memberToken');
          localStorage.removeItem('memberUser');
          if (!currentPath.endsWith('/members/login')) {
            window.location.href = '/members/login';
          }
        } else {
          // Admin context: clear admin token and redirect to admin login
          clearToken();
          if (!window.location.pathname.endsWith("/login")) {
            window.location.href = "/login";
          }
        }
      }
      return Promise.reject(err);
    }
    // Offline mutation → enqueue + return synthetic success
    const cfg = err?.config;
    const sensitiveMutation = /\/(?:auth(?:\/|$)|companion-bookings(?:\/|$))/.test(cfg?.url || "")
      || /\/members\/auth\//.test(cfg?.url || "");
    if (cfg && !sensitiveMutation && !cfg._noOfflineQueue && isMutation(cfg.method) && isOfflineError(err)) {
      try {
        const fullUrl = (cfg.baseURL || "") + cfg.url;
        await enqueueMutation({
          method: (cfg.method || "post").toUpperCase(),
          url: fullUrl,
          headers: { "Content-Type": "application/json", ...(cfg.headers || {}) },
          data: cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : null,
        });
        // Notify listeners
        window.dispatchEvent(new CustomEvent("gio:queue-changed"));
        // Synthetic success — UI keeps working
        return {
          data: { pending: true, queued_at: new Date().toISOString() },
          status: 202,
          statusText: "Accepted (offline queue)",
          headers: {},
          config: cfg,
        };
      } catch (qErr) {
        // eslint-disable-next-line no-console
        console.warn("[offline-queue] enqueue failed", qErr);
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Extract resource type from API URL for auto-refresh
 * Examples:
 *   /api/crm/123 -> crm
 *   /api/videos/ -> videos
 *   /api/calendar/events -> calendar
 */
function extractResourceType(url) {
  if (!url) return null;
  
  // Remove /api/ prefix and extract first segment
  const match = url.match(/^\/?(api\/)?([^\/\?]+)/);
  if (!match || !match[2]) return null;
  
  const resource = match[2];
  
  // Map common resources
  const resourceMap = {
    'crm': 'crm',
    'videos': 'videos',
    'pictures': 'pictures',
    'catalog': 'catalog',
    'calendar': 'calendar',
    'collabs': 'collabs',
    'content': 'content',
    'revenue': 'revenue',
    'finance': 'finance',
    'expenses': 'expenses',
    'studios': 'studios',
    'pipeline': 'pipeline',
    'onlyfans': 'onlyfans',
    'jff': 'jff',
    'x': 'social',
    'social-posts': 'social',
    'platform-stats': 'stats',
    'copilot': 'copilot',
    'landing': 'landing',
  };
  
  return resourceMap[resource] || resource;
}

/** Manually trigger a queue drain (e.g. on `online` event). */
export async function syncOfflineQueue() {
  const result = await drainQueue();
  window.dispatchEvent(new CustomEvent("gio:queue-changed"));
  return result;
}

/* Auto-sync when we come back online */
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { syncOfflineQueue(); });
  // Also sync on focus (in case `online` event missed)
  window.addEventListener("focus", () => {
    if (navigator.onLine) syncOfflineQueue();
  });
}
